import React from 'react';
import { calcBucketCost } from '../../utils/quoteEngine.js';

const card = {
  background: '#fff',
  borderRadius: 10,
  padding: 24,
  marginBottom: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const th = {
  fontSize: 12,
  fontWeight: 600,
  padding: '10px 12px',
  textAlign: 'left',
  background: '#f8fafc',
  borderBottom: '2px solid #e2e8f0',
};

const td = {
  fontSize: 13,
  padding: '10px 12px',
  borderBottom: '1px solid #e2e8f0',
};

const thR = { ...th, textAlign: 'right' };
const tdR = { ...td, textAlign: 'right', fontWeight: 600 };

// Market-rate labor multiplier — Shain's spec: "market price labor should be
// roughly double ADS". Outside shops carry higher labor cost structures.
const MARKET_LABOR_MULTIPLIER = 2.0;

function computeScenario(
  loads, starterType, ADS, MKT,
  infraMat, infraHrs, infraMkt,
  qaHrs, shipHrs,
  laborRate, ohPct, margin, tier
) {
  // calcBucketCost expects lowercase 'soft' / 'vfd' (matches App.jsx's `st` state).
  const starterLc = starterType === 'VFD' ? 'vfd' : 'soft';
  const safeTier = tier ?? 1;
  const loadedRate = laborRate * (1 + ohPct);

  let bktMat = 0;
  let bktHrs = 0;
  let bktMkt = 0;

  loads.forEach((ld) => {
    // Use the same calcBucketCost the rest of the app uses — it knows about
    // the real ADS keys ('SS.3HP', 'VFD.15HP', 'AB.CB.40A', etc.) and produces
    // genuinely different material totals for SS vs VFD.
    const { mat, hrs } = calcBucketCost(starterLc, ld, ADS);
    bktMat += mat || 0;
    bktHrs += hrs || 0;

    const nema = ld.nemaSize ?? ld.nema ?? 1;
    const mktTbl = MKT[starterLc] || {};
    const mktBucket =
      (!ld.hp || ld.hp === 0) ? mktTbl.ctrl
      : (mktTbl[nema] || mktTbl[0] || mktTbl.ctrl);
    bktMkt += (mktBucket && mktBucket[safeTier]) || 0;
  });

  // ADS / our pricing — mirrors App.jsx grand-total logic so the comparison
  // matches the Summary tab when the scenario equals the currently-selected
  // starter type.
  const grandMat = bktMat + (infraMat || 0);
  const grandHrs = bktHrs + (infraHrs || 0) + (qaHrs || 0) + (shipHrs || 0);
  const grandLabor = grandHrs * loadedRate;
  const totalCost = grandMat + grandLabor;
  const sellPrice = totalCost / (1 - margin);
  const gp = sellPrice - totalCost;
  const gpPct = sellPrice > 0 ? gp / sellPrice : 0;

  // Market / open-market equivalent:
  //   materials  = sum of MKT list prices (no team discount)
  //   labor      = same hours × 2 × loaded rate (open-market shops cost more)
  //   margin     = same as ours (rough assumption — competitors target similar GP)
  const marketMat = bktMkt + (infraMkt || 0);
  const marketLabor = grandHrs * MARKET_LABOR_MULTIPLIER * loadedRate;
  const marketCost = marketMat + marketLabor;
  const marketSell = marketCost / (1 - margin);

  return {
    // ADS
    totalMat: grandMat,
    totalLabor: grandLabor,
    totalCost,
    sellPrice,
    gp,
    gpPct,
    grandHrs,
    // Market
    marketMat,
    marketLabor,
    marketCost,
    marketSell,
  };
}

export default function CompareTab({
  loads, encl, ctrl, NS,
  totalInfraMat, totalInfraHrs, totalInfraMkt,
  qaHrs, shipHrs, laborRate, ohPct, margin,
  tier, C, fmt, pct, ADS, MKT,
}) {
  const ss = computeScenario(
    loads, 'SS', ADS, MKT,
    totalInfraMat, totalInfraHrs, totalInfraMkt,
    qaHrs, shipHrs, laborRate, ohPct, margin, tier
  );
  const vfd = computeScenario(
    loads, 'VFD', ADS, MKT,
    totalInfraMat, totalInfraHrs, totalInfraMkt,
    qaHrs, shipHrs, laborRate, ohPct, margin, tier
  );

  const rows = [
    { label: 'Total Material', ssVal: fmt(ss.totalMat), vfdVal: fmt(vfd.totalMat), delta: vfd.totalMat - ss.totalMat },
    { label: 'Total Labor', ssVal: fmt(ss.totalLabor), vfdVal: fmt(vfd.totalLabor), delta: vfd.totalLabor - ss.totalLabor },
    { label: 'Total Cost', ssVal: fmt(ss.totalCost), vfdVal: fmt(vfd.totalCost), delta: vfd.totalCost - ss.totalCost },
    { label: 'Sell Price', ssVal: fmt(ss.sellPrice), vfdVal: fmt(vfd.sellPrice), delta: vfd.sellPrice - ss.sellPrice, bold: true },
    { label: 'Gross Profit', ssVal: fmt(ss.gp), vfdVal: fmt(vfd.gp), delta: vfd.gp - ss.gp },
    { label: 'GP %', ssVal: pct(ss.gpPct), vfdVal: pct(vfd.gpPct), delta: null, isPct: true },
  ];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Starter Type Comparison</div>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          Side-by-side: Soft Starter vs Variable Frequency Drive &nbsp;|&nbsp;
          {loads.length} load{loads.length !== 1 ? 's' : ''} &nbsp;|&nbsp;
          {NS} section{NS !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Comparison Table */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>&nbsp;</th>
              <th style={thR}>Soft Start</th>
              <th style={thR}>VFD</th>
              <th style={thR}>Delta</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.label} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                <td style={{ ...td, fontWeight: 600 }}>{row.label}</td>
                <td style={{ ...tdR, ...(row.bold ? { fontSize: 14 } : {}) }}>{row.ssVal}</td>
                <td style={{ ...tdR, ...(row.bold ? { fontSize: 14 } : {}) }}>{row.vfdVal}</td>
                <td
                  style={{
                    ...tdR,
                    color: row.delta == null
                      ? '#64748b'
                      : row.delta > 0
                        ? (C.red || '#ef4444')
                        : row.delta < 0
                          ? (C.green || '#16a34a')
                          : '#64748b',
                  }}
                >
                  {row.delta == null
                    ? '--'
                    : `${row.delta >= 0 ? '+' : ''}${fmt(row.delta)}`
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ADS vs Market — per scenario */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>ADS Pricing vs Market Pricing</div>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 14 }}>
          Market materials at published wholesale (no team discounts). Market labor
          at {MARKET_LABOR_MULTIPLIER.toFixed(1)}× our loaded rate to reflect
          higher-cost shops.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {[
            { label: 'Soft Start', data: ss, color: C.blue || '#2563eb' },
            { label: 'VFD', data: vfd, color: C.orange || '#f59e0b' },
          ].map((scenario) => {
            const adsSell = scenario.data.sellPrice;
            const mktSell = scenario.data.marketSell;
            const diff = adsSell - mktSell;       // positive = we're over market
            const under = diff <= 0;
            return (
              <div
                key={scenario.label}
                style={{
                  padding: 16,
                  borderRadius: 8,
                  border: `1px solid #e2e8f0`,
                  borderTop: `3px solid ${scenario.color}`,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: scenario.color }}>
                  {scenario.label}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left',  padding: '4px 0', color: '#64748b', fontWeight: 600 }}></th>
                      <th style={{ textAlign: 'right', padding: '4px 0', color: '#64748b', fontWeight: 600 }}>ADS</th>
                      <th style={{ textAlign: 'right', padding: '4px 0', color: '#64748b', fontWeight: 600 }}>Market</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '3px 0', color: '#374151' }}>Material</td>
                      <td style={{ padding: '3px 0', textAlign: 'right' }}>{fmt(scenario.data.totalMat)}</td>
                      <td style={{ padding: '3px 0', textAlign: 'right' }}>{fmt(scenario.data.marketMat)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '3px 0', color: '#374151' }}>Labor</td>
                      <td style={{ padding: '3px 0', textAlign: 'right' }}>{fmt(scenario.data.totalLabor)}</td>
                      <td style={{ padding: '3px 0', textAlign: 'right' }}>{fmt(scenario.data.marketLabor)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '3px 0', color: '#374151' }}>Cost</td>
                      <td style={{ padding: '3px 0', textAlign: 'right' }}>{fmt(scenario.data.totalCost)}</td>
                      <td style={{ padding: '3px 0', textAlign: 'right' }}>{fmt(scenario.data.marketCost)}</td>
                    </tr>
                    <tr style={{ borderTop: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '6px 0', fontWeight: 700 }}>Sell</td>
                      <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 700, fontSize: 13 }}>{fmt(adsSell)}</td>
                      <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 700, fontSize: 13 }}>{fmt(mktSell)}</td>
                    </tr>
                  </tbody>
                </table>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    marginTop: 8,
                    color: under ? (C.green || '#16a34a') : (C.red || '#ef4444'),
                  }}
                >
                  {under
                    ? `${fmt(Math.abs(diff))} under market`
                    : `${fmt(diff)} over market`
                  }
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
